import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import cn from 'classnames';
import { createStructuredSelector } from 'reselect';
import { connect, useDispatch } from 'react-redux';
import { compose } from 'redux';
import get from 'lodash/get';
import { useRouter } from 'next/router';
import SearchIcon from '@material-ui/icons/Search';
import { debounce } from 'lodash';
import { useTranslation, Router } from '../../../../src/i18n';

import {
  getCompanyUnpublishedSeasonAction,
  getCompanySeasonsValueAction,
  getMusicalWorksListAction,
  getCompanyEditSeasonAction,
  clearCompanyEditSeason,
} from '../../actions';
import {
  selectCompanySeasonsOptions,
  selectCompanyUpdatedProductions,
  selectCompanyUnpublishedProductions,
  selectCompanyMusicalWorksList,
  selectCompanyEditProductions,
} from '../../selectors';

import FbCircularProgress from '../../../../components/FbCircularProgress';
import Button from '../../../../components/Button';
import EditProductionCard from '../../../../components/EditProductionCard';
import {
  TP,
  PRODUCTION_ADDED_IDENTIFIERS_IDS,
  PRIVACY_ATTRIBUTE_IDS,
  performancesAggregationTypes,
  performancesAddedByIdentifiers,
  PERFORMANCE_TYPES_ID,
} from '../../../../constants/consts';
import {
  canDoProductionAction,
  getRouteWithCountryAndSlug,
  checkDateForProductionDeletion,
} from '../../../../utils/common';
import FormikAutocompleteSelect from '../../../../components/Fields/FormikAutocompleteSelect';
import { updateOne, deleteEntity, createEntity, useBufferValueAction, getList } from '../../../App/actions';
import { FILTER_AGGREGATION_SEARCH, PRODUCTIONS } from '../../../../constants/endpointConstants';
import route, { companiesPaths } from '../../../../constants/routes';
import { defaultCompanyProductionsPagination, PRODUCTIONS_TYPES } from '../../constants';
import PublishProductionModal from '../../../../components/EditComponents/PublishProductionModal';
import ConfirmDeleteModal from '../../../../components/EditComponents/ConfirmDeleteModal';
import FbPagination from '../../../../components/FbPagination';
import { selectActiveProfileId, selectBufferValueData } from '../../../App/selectors';
import usePermissions from '../../../../utils/permissions';
import ActionStatusModal from '../../../../components/ActionStatusModal';
import H3 from '../../../../components/H3';
import TextButton from '../../../../components/TextButton';
import { colors } from '../../../../constants/styleConstants';
import { selectUserConstants } from '../../../../store/selectors';

const useStyles = makeStyles(theme => ({
  title: {
    marginBottom: 24,
  },
  actionBlock: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    marginBottom: 24,
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'center',
    },
  },
  selectsBox: {
    flex: 1,
    width: '100%',
  },
  field: {
    maxWidth: 165,
    display: 'inline-block',
    marginRight: 10,
    [theme.breakpoints.down('xs')]: {
      width: '100%',
      maxWidth: '100%',
      marginBottom: theme.spacing(2),
    },
  },
  searchField: {
    maxWidth: 340,
    [theme.breakpoints.down('xs')]: {
      maxWidth: '100%',
    },
  },
  productionCard: {
    marginBottom: 20,
  },
}));

const { published, unpublished } = PRODUCTIONS_TYPES;
const { companyAgg, seasonAgg, workAgg, typeAgg, queryAgg } = performancesAggregationTypes;

const EditSeasons = ({
  className,
  companyData,
  productions,
  seasonsOptions,
  updatedProductions,
  unpublishedProductions,
  publishingProductionData,
  musicalWorksList,
  activeProfileId,
  userConstants,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const urlQuery = useRouter().query;
  const { productionPermissions, isAdmin } = usePermissions(activeProfileId);

  const allSeasonsOption = { value: '', label: t(`${TP}.m_SEASON_ALL`) };
  const allSeasonsOptions = [allSeasonsOption, ...seasonsOptions];
  const productionsTypeOptions = [
    {
      value: published,
      label: t(`${TP}.FN_EDITSEASON_PUBLISHED_STATUS`),
    },
    {
      value: unpublished,
      label: t(`${TP}.FN_EDITSEASON_UNPUBLISHED_STATUS`),
    },
  ];

  const [seasonValue, setSeasonValue] = React.useState(allSeasonsOption);
  const [interactedProduction, setInteractedProduction] = React.useState(null);
  const [deletedProduction, setDeletedProduction] = React.useState(null);
  // productions.data.sort((prod1, prod2) => new Date(prod2.updatedAt) - new Date(prod1.updatedAt)); should be added on backend
  const companyId = companyData?.id;
  const [productionsType, setProductionsType] = React.useState(
    productionsTypeOptions.find(option => option?.value === urlQuery['productions-type']) || productionsTypeOptions[0],
  );
  const [mwValue, setMvValue] = React.useState(null);
  const [mwValueSearching, setMvValueSearching] = React.useState(false);
  const [productionToCancel, setProductionToCancel] = React.useState(null);

  const getUnpublishedProductions = (id, pagination = defaultCompanyProductionsPagination, query) => {
    dispatch(
      getCompanyUnpublishedSeasonAction({
        companyId: id,
        pagination,
        query,
      }),
    );
  };

  React.useEffect(() => {
    if (!productions?.data?.length) {
      dispatch(
        getList({
          endpoint: FILTER_AGGREGATION_SEARCH(PRODUCTIONS),
          sagaRoutine: getCompanySeasonsValueAction,
          params: {
            queryParams: { aggregation_type: 'season_id', [performancesAggregationTypes.companyAgg]: companyId },
          },
        }),
      );
      if (productionsType?.value === PRODUCTIONS_TYPES.published) {
        dispatch(getCompanyEditSeasonAction({ companyId, pagination: defaultCompanyProductionsPagination }));
      } else {
        getUnpublishedProductions(companyId);
      }
    }
    setSeasonValue(allSeasonsOptions[0]);

    return () => {
      dispatch(clearCompanyEditSeason());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const seasonChange = value => {
    setSeasonValue(value);
    dispatch(
      getCompanyEditSeasonAction({
        companyId: get(companyData, 'id'),
        seasonId: get(value, 'value'),
        pagination: defaultCompanyProductionsPagination,
        query: { [workAgg]: mwValue?.value },
      }),
    );
  };

  const updateProduction = (productionId, options) => {
    dispatch(
      updateOne({
        endpoint: PRODUCTIONS,
        id: productionId,
        params: options,
        sagaRoutine: useBufferValueAction,
        callback: () => getProductions(defaultCompanyProductionsPagination),
      }),
    );
  };

  const publishProduction = () => {
    if (interactedProduction) {
      dispatch(
        createEntity({
          endpoint: `${PRODUCTIONS}/${interactedProduction.id}/actions`,
          sagaRoutine: useBufferValueAction,
          params: {
            type: 'publish',
            addedBy: { id: PRODUCTION_ADDED_IDENTIFIERS_IDS.COMPANY },
            approvedBy: { id: PRODUCTION_ADDED_IDENTIFIERS_IDS.COMPANY },
            privacyAttribute: {
              id: PRIVACY_ATTRIBUTE_IDS.PUBLIC,
            },
            company: {
              id: companyData?.id,
            },
          },
          callback: () => {
            getUnpublishedProductions(get(companyData, 'id'));
            setInteractedProduction(null);
          },
        }),
      );
    }
  };

  const deleteProduction = () => {
    if (deletedProduction) {
      if (deletedProduction?.type === 'draft') {
        dispatch(
          deleteEntity({
            endpoint: PRODUCTIONS,
            id: deletedProduction?.data?.id,
            callback: () => getProductions(defaultCompanyProductionsPagination),
          }),
        );
      } else if (deletedProduction?.type === 'published') {
        updateProduction(deletedProduction?.data?.id, {
          companies: deletedProduction?.data?.companies?.filter(c => c?.id !== companyData?.id),
        });
      }
    }
    setDeletedProduction(null);
  };

  const switchProductionsType = type => {
    const chosenType = type?.value;
    if (chosenType !== productionsType?.value) {
      Router.push(
        companiesPaths.edit,
        `${getRouteWithCountryAndSlug(route.COMPANIES, companyData)}/edit?tab=seasons&productions-type=${chosenType}`,
        { shallow: true },
      );
      setProductionsType(type);
      if (chosenType === PRODUCTIONS_TYPES.published) {
        dispatch(
          getCompanyEditSeasonAction({
            companyId: companyData?.id,
            pagination: defaultCompanyProductionsPagination,
            query: { [workAgg]: mwValue?.value },
          }),
        );
      } else {
        // published changed on unpublished => reset season option
        setSeasonValue(allSeasonsOption);
        getUnpublishedProductions(companyData?.id, defaultCompanyProductionsPagination, { [workAgg]: mwValue?.value });
      }
    }
  };

  const getWorksList = debounce(value => {
    dispatch(
      getList({
        endpoint: FILTER_AGGREGATION_SEARCH(PRODUCTIONS),
        sagaRoutine: getMusicalWorksListAction,
        params: {
          queryParams: {
            ...(productionsType?.value !== PRODUCTIONS_TYPES.unpublished ? { [seasonAgg]: seasonValue?.value } : {}), // allow in future add both option
            ...(productionsType?.value === PRODUCTIONS_TYPES.unpublished
              ? { added_by: performancesAddedByIdentifiers.COMPANY }
              : {}), // how to deal with both??
            [companyAgg]: companyData?.id,
            [typeAgg]: workAgg,
            [queryAgg]: value || '',
            published: productionsType?.value === PRODUCTIONS_TYPES.published,
          },
        },
      }),
    );
  }, 500);

  const getProductions = (pagination, query) => {
    if (productionsType?.value === PRODUCTIONS_TYPES.published) {
      dispatch(
        getCompanyEditSeasonAction({
          companyId: companyData?.id,
          seasonId: seasonValue?.value,
          pagination,
          query,
        }),
      );
    } else {
      getUnpublishedProductions(companyData?.id, pagination, query);
    }
  };

  const setMusicalWorkValue = option => {
    setMvValue(option);
    getProductions(defaultCompanyProductionsPagination, { [workAgg]: option?.value || '' });
  };

  const cancelProduction = () => {
    updateProduction(productionToCancel?.id, {
      performanceTypes: [...productionToCancel?.performanceTypes, { id: PERFORMANCE_TYPES_ID.CANCELLED }],
    });
    setProductionToCancel(null);
  };

  React.useEffect(() => {
    setMvValueSearching(false);
  }, [musicalWorksList]);

  const checkProductionAction = (permission, production) =>
    canDoProductionAction(permission, PRODUCTION_ADDED_IDENTIFIERS_IDS.COMPANY, companyData?.id, production);

  const canDeletePublishedProduction = production =>
    productionPermissions?.canDeleteAsAdmin ||
    (checkProductionAction(productionPermissions?.canDelete, production) &&
      checkDateForProductionDeletion(
        get(userConstants, '[0].MINIMUM_PRODUCTION_DELETION_DATE'),
        production?.published_at,
      ));

  return (
    <div className={cn(classes.root, className)}>
      <main>
        <div className={classes.actionBlock}>
          <div className={classes.selectsBox}>
            <FormikAutocompleteSelect
              instanceId="productions_type"
              field={{
                name: 'productions_type',
                value: productionsType,
              }}
              onChange={switchProductionsType}
              options={productionsTypeOptions}
              preventDefaultOnChange
              className={classes.field}
              label={t(`${TP}.M_INTRO_VIEW`)}
            />
            <FormikAutocompleteSelect
              instanceId="season_date"
              field={{
                name: 'season_date',
                value: seasonValue,
              }}
              onChange={seasonChange}
              options={productionsType?.value === published ? allSeasonsOptions : [allSeasonsOption]}
              preventDefaultOnChange
              className={classes.field}
              label={t(`${TP}.m_CHOOSESEASON`)}
            />
            <FormikAutocompleteSelect
              className={cn(classes.field, classes.searchField)}
              instanceId="musical_work_search"
              label={t(`${TP}.m_MUSICALWORK`)}
              field={{
                name: 'musical_work',
                value: mwValue,
              }}
              placeholder={t(`${TP}.FN_SEARCH_BY_MUSICAL_WORK`)}
              onChange={setMusicalWorkValue}
              onInputTextChange={v => {
                getWorksList(v);
                setMvValueSearching(true);
              }}
              options={musicalWorksList?.options || []}
              isLoading={musicalWorksList?.loading}
              isClearable
              isEditable
              customComponents={{
                DropdownIndicator: () => <SearchIcon />,
              }}
              onFocus={() => {
                getWorksList('');
                setMvValueSearching(true);
              }}
              startAggregations={mwValueSearching}
            />
          </div>
          <Button
            onClick={() =>
              Router.push(
                companiesPaths.addProduction,
                `${getRouteWithCountryAndSlug(route.COMPANIES, companyData)}/edit/modify-production?step=1`,
              )
            }
          >
            {t(`${TP}.m_ADDSCHEDULE`)}
          </Button>
        </div>
        {/* TODO update in one render after 'search in both' will be added */}
        {productionsType?.value === published && (
          <>
            {productions?.loading ? (
              <FbCircularProgress />
            ) : (
              <>
                {(productions?.data || []).map(p => (
                  <EditProductionCard
                    production={p}
                    key={p?.id}
                    onCancel={
                      checkProductionAction(productionPermissions?.canCancel, p) ? () => setProductionToCancel(p) : null
                    }
                    className={classes.productionCard}
                    updatedProduction={updatedProductions[p?.id]}
                    editProductionLinkProps={step => ({
                      pathname: `${getRouteWithCountryAndSlug(route.COMPANIES, companyData)}/edit/modify-production`,
                      query: { production_id: p?.id, step },
                    })}
                    onDelete={
                      canDeletePublishedProduction(p)
                        ? () => setDeletedProduction({ data: p, type: 'published' })
                        : null
                    }
                  />
                ))}
                <FbPagination filters={productions} fetchFunction={getProductions} />
                {!productions?.loading &&
                  productions?.loadedOnce &&
                  productions?.data?.length === 0 &&
                  t(`${TP}.NO_RESULTS`)}
              </>
            )}
          </>
        )}
        {productionsType?.value === unpublished &&
          (unpublishedProductions?.loading ? (
            <FbCircularProgress />
          ) : (
            <>
              {(unpublishedProductions?.data || []).map(p => (
                <EditProductionCard
                  production={p}
                  key={p?.id}
                  onCancel={
                    isAdmin || checkProductionAction(productionPermissions?.canCancel, p)
                      ? () => setProductionToCancel(p)
                      : null
                  }
                  className={classes.productionCard}
                  updatedProduction={updatedProductions[p?.id]}
                  editProductionLinkProps={step => ({
                    pathname: `${getRouteWithCountryAndSlug(route.COMPANIES, companyData)}/edit/modify-production`,
                    query: { production_id: p?.id, step },
                  })}
                  onPublish={isAdmin || productionPermissions.canPublishAll ? () => setInteractedProduction(p) : null}
                  onDelete={
                    isAdmin || checkProductionAction(productionPermissions?.canDelete, p)
                      ? () => setDeletedProduction({ data: p, type: 'draft' })
                      : null
                  }
                />
              ))}
              <FbPagination filters={unpublishedProductions} fetchFunction={getProductions} />
              {!unpublishedProductions?.loading &&
                unpublishedProductions?.loadedOnce &&
                unpublishedProductions?.data?.length === 0 &&
                t(`${TP}.NO_RESULTS`)}
            </>
          ))}
      </main>
      {interactedProduction?.id && (
        <PublishProductionModal
          production={interactedProduction}
          closeModal={() => setInteractedProduction(null)}
          publish={publishProduction}
          isPublishing={publishingProductionData?.loading}
        />
      )}
      {deletedProduction?.data?.id && (
        <ConfirmDeleteModal
          closeModal={() => setDeletedProduction(null)}
          onDelete={deleteProduction}
          title={t(`${TP}.FN_EDITSEASON_APPROVE_DELETE_PRODUCTION_TITLE`, { name: deletedProduction?.name })}
          text={t(`${TP}.FN_EDITSEASON_APPROVE_DELETE_PRODUCTION_INFO`)}
        />
      )}
      {productionToCancel && (
        <ActionStatusModal
          isOpened
          onClose={() => setProductionToCancel(null)}
          type="warning"
          title={
            <H3>{t(`${TP}.FN_EDITSEASON_APPROVE_CANCELE_PRODUCTION_TITLE`, { name: productionToCancel?.name })}</H3>
          }
          buttons={[
            <TextButton
              onClick={() => setProductionToCancel(null)}
              style={{ color: colors.text, fontWeight: 'bold', padding: '0.73rem' }}
              key="approve-button"
            >
              {t(`${TP}.m_CANCEL`)}
            </TextButton>,
            <Button onClick={cancelProduction} key="cancel-button">
              {t(`${TP}.FN_EDITSEASON_MARK_AS_CANCELLED`)}
            </Button>,
          ]}
        >
          {t(`${TP}.FN_EDITSEASON_APPROVE_CANCELE_PRODUCTION_INFO`)}
        </ActionStatusModal>
      )}
    </div>
  );
};

EditSeasons.propTypes = {
  className: PropTypes.string,
  productions: PropTypes.object,
  seasonsOptions: PropTypes.array,
  companyData: PropTypes.object,
  updatedProductions: PropTypes.object,
  unpublishedProductions: PropTypes.object,
  musicalWorksList: PropTypes.shape({
    loading: PropTypes.bool,
    options: PropTypes.array,
  }),
};

const mapStateToProps = createStructuredSelector({
  activeProfileId: selectActiveProfileId(),
  productions: selectCompanyEditProductions(),
  unpublishedProductions: selectCompanyUnpublishedProductions(),
  seasonsOptions: selectCompanySeasonsOptions(),
  updatedProductions: selectCompanyUpdatedProductions(),
  publishingProductionData: selectBufferValueData(),
  musicalWorksList: selectCompanyMusicalWorksList(),
  userConstants: selectUserConstants(),
});

const withConnect = connect(mapStateToProps, null);

export default compose(memo, withConnect)(EditSeasons);
